import { watch, onMounted, type Ref, onUnmounted } from 'vue';

/**
 * Composable function to manage clicking outside elements and trigger associated actions.
 *
 * This composable adds a background element behind the specified component (identified by base class name)
 * and listens for clicks on it. When clicked, the associated action is triggered by calling the provided
 * callback function. This is commonly used to handle scenarios where a popover, modal, or any component
 * needs to react to clicks outside of it.
 *
 * @param {string} elementBaseClassName - The base class name of the component to which the background element will be added.
 * @param {Ref<boolean>} isOpen - A ref representing the state of the associated component (e.g., open or closed).
 * @param {() => void} onBackgroundClick - Callback function to be executed when a click occurs outside the component.
 */
export const useElementBackgroundClick = (
	elementBaseClassName: string,
	isOpen: Ref<boolean>,
	onBackgroundClick: () => void
) => {
	const backgroundClassName = `${elementBaseClassName}__background`;

	const createBackgroundElement = () => {
		const background = document.createElement('div');

		background.className = backgroundClassName;
		background.style.zIndex = '102';
		background.style.top = '0';
		background.style.left = '0';
		background.style.right = '0';
		background.style.bottom = '0';
		background.style.position = 'fixed';

		background.setAttribute('role', 'presentation');
		background.addEventListener('click', onBackgroundClick);
		document.body.appendChild(background);
	};

	const removeBackgroundElement = () => {
		const background = document.querySelector(`.${backgroundClassName}`);

		if (background) {
			background.removeEventListener('click', onBackgroundClick);
			background.remove();
		}
	};

	watch(isOpen, (newValue) => {
		if (newValue) {
			createBackgroundElement();
		} else {
			removeBackgroundElement();
		}
	});

	onMounted(() => {
		if (isOpen.value) {
			createBackgroundElement();
		}
	});

	onUnmounted(removeBackgroundElement);
};
